import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { AxiosService, OnlineService } from './index';
import { LoggerService } from '@/utils';
import { DexieMyMillUnitItem } from '@/models/dexie/myMill/DexieMyMillUnitItem';
import { DexieMyMillUnitStatus, IDexieMyMillUnitStatus } from '@/models/dexie/myMill/DexieMyMillUnitStatus';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AuthStore } from '@/store/store.auth';
import { ApiEndpoints } from '@/config';
import { MyMillUnitStatus } from '@/models/myMill/MyMillUnitStatus';
import { Helpers } from '@/helpers/Helpers';
import { MyMillUnitItem } from '@/models/myMill/MyMillUnitItem';
import { ILiteEvent, LiteEvent } from '@/models/event/liteEvent';
import { MyMillMachineItem } from '@/models/myMill/MyMillMachineItem';
import { ConfigurationValueSelect } from '@/models/configuration/ValuesSelect';

class MyMillDataProvider {
    public myMillUnits: ConfigurationValueSelect[] = [];
    public myMillFilteredUnits: ConfigurationValueSelect[] = [];
    public myMillSelectedUnit: string | undefined = undefined;
    public myMillSelectedUnitData: MyMillUnitItem | undefined = undefined;
    public myMillSelectedUnitMachines: ConfigurationValueSelect[] = [];

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;
    private readonly onMyMillUnitsChanged = new LiteEvent<ConfigurationValueSelect[]>();
    private readonly onMyMillSelectedUnitChanged = new LiteEvent<ConfigurationValueSelect[]>();
    private readonly onMyMillSelectedUnitMachinesChanged = new LiteEvent<ConfigurationValueSelect[]>();
    private readonly onMyMillGetUnitssStarted = new LiteEvent<void>();
    private readonly onMyMillFilterUnitsStarted = new LiteEvent<void>();
    private readonly onMyMillFilterMachinesStarted = new LiteEvent<void>();

    private selectedFilterString: string | undefined = undefined;
    // tslint:disable-next-line: no-empty
    constructor() {}

    public get OnMyMillUnitsChanged(): ILiteEvent<ConfigurationValueSelect[]> {
        return this.onMyMillUnitsChanged.expose();
    }

    public get OnMyMillSelectedUnitChanged(): ILiteEvent<ConfigurationValueSelect[]> {
        return this.onMyMillSelectedUnitChanged.expose();
    }

    public get OnMyMillSelectedUnitMachinesChanged(): ILiteEvent<ConfigurationValueSelect[]> {
        return this.onMyMillSelectedUnitMachinesChanged.expose();
    }

    public get OnMyMillGetUnitsStarted(): ILiteEvent<void> {
        return this.onMyMillGetUnitssStarted.expose();
    }

    public get OnMyMillFilterUnitsStarted(): ILiteEvent<void> {
        return this.onMyMillFilterUnitsStarted.expose();
    }

    public get OnMyMillFilterMachinesStarted(): ILiteEvent<void> {
        return this.onMyMillFilterMachinesStarted.expose();
    }

    /**
     * Get all unit items from dexie db
     */
    public async GetAllUnitItems(): Promise<DexieMyMillUnitItem[]> {
        return this.dexieDB.getTable<DexieMyMillUnitItem>(this.dexieDB!.getTables().myMillUnitItems).toArray();
    }
    /**
     * Set new units items
     * @param inDexieMyMillUnitItems
     */
    public async SetNewUnitItems(inDexieMyMillUnitItems: DexieMyMillUnitItem[]): Promise<number> {
        return this.dexieDB.bulkPut<DexieMyMillUnitItem>(this.dexieDB!.getTables().myMillUnitItems, inDexieMyMillUnitItems);
    }
    /**
     * Delete all units items from dexie db
     */
    public async DeleteAllUnitItems(): Promise<void> {
        return this.dexieDB.clear<DexieMyMillUnitItem>(this.dexieDB!.getTables().myMillUnitItems);
    }

    /**
     * Set/update the last update date for the mymill data
     * @param inUpdate
     */
    public async SetLastUpdateDate(inUpdate: DexieMyMillUnitStatus): Promise<number> {
        return this.dexieDB.put<DexieMyMillUnitStatus>(this.dexieDB!.getTables().myMillUnitStatus, inUpdate);
    }

    public async GetAllUnitNames(): Promise<ConfigurationValueSelect[]> {
        this.onMyMillGetUnitssStarted.trigger();
        this.selectedFilterString = undefined;

        return new Promise<ConfigurationValueSelect[]>((resolve, reject) => {
            this.myMillUnits = [];
            const data: ConfigurationValueSelect[] = [];

            this.GetAllUnitItems()
                .then((allUnits) => {
                    allUnits.forEach((unit) => {
                        data.push(
                            new ConfigurationValueSelect({
                                text: unit.name,
                                value: unit.uuid,
                                subText: unit.customerName,
                            }),
                        );
                    });
                    this.myMillUnits = data;
                    this.onMyMillUnitsChanged.trigger(data);
                    resolve(data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    public SetFilteredMyMillUnits(inFilterString: string): Promise<ConfigurationValueSelect[]> {
        this.onMyMillFilterUnitsStarted.trigger();
        this.selectedFilterString = inFilterString;

        return new Promise<ConfigurationValueSelect[]>((resolve, reject) => {
            this.myMillFilteredUnits = [];
            const filteredData: ConfigurationValueSelect[] = [];
            this.GetAllUnitItems()
                .then((units) => {
                    units
                        .filter((funit) => funit.machines.some((m) => m.serialNumber.includes(inFilterString)))
                        .forEach((unit) => {
                            filteredData.push(
                                new ConfigurationValueSelect({
                                    text: unit.name,
                                    value: unit.uuid,
                                    subText: unit.customerName,
                                }),
                            );
                        });
                    this.myMillFilteredUnits = filteredData;
                    this.onMyMillUnitsChanged.trigger(filteredData);

                    resolve(filteredData);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    public async SetSelectedUnit(inUnitUuid?: string) {
        this.myMillSelectedUnit = inUnitUuid != null ? inUnitUuid : undefined;
        this.myMillSelectedUnitMachines = [];

        if (inUnitUuid != null) {
            await this.dexieDB
                .getTable<DexieMyMillUnitItem>(this.dexieDB!.getTables().myMillUnitItems)
                .where('uuid')
                .equals(inUnitUuid)
                .first((unit) => {
                    this.myMillSelectedUnitData = unit;
                });

            if (this.myMillSelectedUnitData) {
                let machines = this.myMillSelectedUnitData.machines;
                if (this.selectedFilterString != null) {
                    machines = this.myMillSelectedUnitData.machines.filter((fm) => fm.serialNumber.includes(this.selectedFilterString || ''));
                }
                machines.forEach((machine) => {
                    this.myMillSelectedUnitMachines.push(
                        new ConfigurationValueSelect({
                            text: machine.name,
                            value: machine.id,
                        }),
                    );
                });
            }
        }
        this.onMyMillSelectedUnitChanged.trigger(this.myMillSelectedUnitMachines);
    }

    public UpdateMyMillUnits(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.SyncMyMillUnitStatus()
                .then((response) => {
                    if (response.update) {
                        LoggerService.debug(`my mill units have an update`);
                        this.SyncMyMillUnits(response.unitStatus)
                            .then(() => {
                                resolve();
                            })
                            .catch((err) => {
                                LoggerService.error('err');
                                reject(err);
                            });
                    } else {
                        LoggerService.debug(`my mill units have no updates`);
                        resolve();
                    }
                })
                .catch((err) => {
                    LoggerService.error(`error on check my mill units for updates: `, err);
                    reject(err);
                });
        });
    }

    private SyncMyMillUnitStatus(): Promise<{ update: boolean; unitStatus?: DexieMyMillUnitStatus }> {
        const tableStrings = this.dexieDB.getTables();

        return new Promise<{ update: boolean; unitStatus?: DexieMyMillUnitStatus }>((resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
                AxiosService.get(ApiEndpoints.myMillUnitStatus, null)
                    .then(async (response) => {
                        if (response.data) {
                            const onlineUnitStatus = new MyMillUnitStatus(response.data);

                            let lastDexieUnitStatus: DexieMyMillUnitStatus | undefined;
                            const unitStatusTable = this.dexieDB.getTable<IDexieMyMillUnitStatus>(tableStrings.myMillUnitStatus);
                            if (unitStatusTable !== undefined) {
                                await unitStatusTable
                                    .get({ id: 1 })
                                    .then((data) => {
                                        if (data != null) {
                                            lastDexieUnitStatus = new DexieMyMillUnitStatus(data);
                                        }
                                    })
                                    .catch((err) => {
                                        reject(err);
                                    });
                            }

                            if (lastDexieUnitStatus !== undefined && onlineUnitStatus.updatedAt && Helpers.compareDateStrings(onlineUnitStatus.updatedAt, lastDexieUnitStatus.updatedAt) > 0) {
                                lastDexieUnitStatus.lastHash = onlineUnitStatus.lastHash;
                                lastDexieUnitStatus.syncedAt = onlineUnitStatus.syncedAt;
                                lastDexieUnitStatus.updatedAt = onlineUnitStatus.updatedAt;
                                resolve({ update: true, unitStatus: lastDexieUnitStatus });
                            } else {
                                if (lastDexieUnitStatus != null) {
                                    resolve({ update: false });
                                } else if (onlineUnitStatus != null) {
                                    const dexiUnitStatus = new DexieMyMillUnitStatus(onlineUnitStatus);
                                    resolve({ update: true, unitStatus: dexiUnitStatus });
                                } else {
                                    reject('lastDexieUnitStatus is null');
                                }
                            }
                        }
                    })
                    .catch((err) => {
                        reject(err);
                    });
            } else {
                LoggerService.LogWarn(`App app seems to be offline, or user is not logged in`, OnlineStatusEnums[OnlineService.GetOnlineStatus], AuthStore.GetIsLoggedInText);
            }
        });
    }

    private SyncMyMillUnits(inUnitStatus?: DexieMyMillUnitStatus): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            /**
             * Fetch new units from API
             */
            AxiosService.get(ApiEndpoints.myMillUnits, null)
                .then((response) => {
                    if (response != null && response.data != null) {
                        const newUnitItems: DexieMyMillUnitItem[] = [];
                        response.data.forEach((unitItem: any) => {
                            const newItem = new MyMillUnitItem(unitItem);
                            /**
                             * rename id to uuid till API send it as uuid
                             */
                            if (unitItem.id) {
                                newItem.uuid = unitItem.id;
                            }
                            newUnitItems.push(new DexieMyMillUnitItem(newItem));
                        });

                        /**
                         * First delete all old units
                         */
                        this.DeleteAllUnitItems()
                            .then(() => {
                                /**
                                 * Now save all unit items new
                                 */
                                this.SetNewUnitItems(newUnitItems)
                                    .then(() => {
                                        if (inUnitStatus) {
                                            this.SetLastUpdateDate(inUnitStatus)
                                                .then(() => {
                                                    resolve();
                                                })
                                                .catch((err) => {
                                                    reject(err);
                                                });
                                        } else {
                                            LoggerService.warn(`MyMill unit items updated, but no new status was given to save.`);
                                            resolve();
                                        }
                                    })
                                    .catch((err) => {
                                        reject(err);
                                    });
                            })
                            .catch((err) => {
                                reject(err);
                            });
                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }
}

export default MyMillDataProvider;
